// Carlyle Avenue — highlight-scroll-band prev/next arrows.
// Progressive enhancement for the `highlight-scroll-band` section: the track is a native
// horizontally-scrollable region on its own (keyboard + swipe), so this only wires the optional
// desktop arrows to scrollBy() and reflects scroll position by marking the arrow spent at each end.
//
// <highlight-scroll-band> with [data-hsb-track], [data-hsb-prev], [data-hsb-next].
//
// LTR-only: the scrollBy direction and end-of-scroll detection assume scrollLeft increases
// left-to-right. The storefront is LTR; revisit (logical scroll / direction check) if an RTL locale ships.

import { bindScrollArrows } from '@theme/ca-scroll-arrows';
import { prefersReducedMotion } from '@theme/utilities';

class HighlightScrollBand extends HTMLElement {
  #autoplayMs = 0;
  #timer = null;
  #arrows = null;
  #io = null;
  #activated = false;

  connectedCallback() {
    this.track = this.querySelector('[data-hsb-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-hsb-prev]');
    this.next = this.querySelector('[data-hsb-next]');

    this.#activateWhenVisible();
  }

  // Defer the first layout read until the band is actually near the viewport. Measured 2026-07-31
  // (Long Animation Frame API): this component's startup cost was 17ms, of which 16ms was forced
  // style+layout — and on the homepage the band sits below the fold, so that reflow bought nothing
  // at load. Lighthouse's mobile CPU emulation multiplies it, which is where TBT comes from. Arrows
  // and autoplay are deferred with it: nobody can perceive arrows appearing on an off-screen
  // carousel, and autoplaying one is wasted work.
  #activateWhenVisible() {
    if (typeof IntersectionObserver !== 'function') {
      this.#activate();
      return;
    }
    this.#io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.#io?.disconnect();
        this.#io = null;
        this.#activate();
      },
      // Start early enough that everything is settled before the band is actually looked at.
      { rootMargin: '200px' }
    );
    this.#io.observe(this);
  }

  #activate() {
    if (this.#activated) return;
    this.#activated = true;

    const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;

    // Arrows are optional progressive enhancement; only wire + reveal them when both
    // are present and there's actually something to scroll.
    if (this.prev && this.next && scrollable) {
      this.prev.hidden = false;
      this.next.hidden = false;
      this.#arrows = bindScrollArrows(this.track, this.prev, this.next, { step: () => this.#step() });
    }

    this.#setupAutoplay(scrollable);
  }

  disconnectedCallback() {
    this.#arrows?.disconnect();
    this.#arrows = null;
    this.#activated = false;
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#io?.disconnect();
    this.#io = null;
    this.#stopAutoplay();
  }

  // Optional auto-scroll: `data-hsb-autoplay="<seconds>"`. Off when absent/0, when
  // there's nothing to scroll, or when the visitor prefers reduced motion. Pauses on
  // hover/focus/touch and while the tab is hidden; loops back to the start at the end.
  #setupAutoplay(scrollable) {
    const seconds = parseFloat(this.getAttribute('data-hsb-autoplay') || '0');
    if (!seconds || !scrollable) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#autoplayMs = seconds * 1000;

    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    this.track.addEventListener('pointerdown', this.#pause);
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#resume();
  }

  #onVisibility = () => {
    if (document.hidden) this.#pause();
    else this.#resume();
  };

  #resume = () => {
    if (!this.#autoplayMs || this.#timer) return;
    this.#timer = setInterval(() => this.#autoTick(), this.#autoplayMs);
  };

  #pause = () => {
    if (!this.#timer) return;
    clearInterval(this.#timer);
    this.#timer = null;
  };

  #stopAutoplay() {
    this.#autoplayMs = 0;
    this.#pause();
  }

  #autoTick() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (this.track.scrollLeft >= max - 2) {
      this.track.scrollTo({ left: 0, behavior: this.#behavior() });
    } else {
      this.#scroll(1);
    }
  }

  // Advance by roughly a viewport-width of tiles, snapped to a sensible step.
  #step() {
    return Math.max(this.track.clientWidth * 0.85, 240);
  }

  #scroll(dir) {
    this.track.scrollBy({ left: dir * this.#step(), behavior: this.#behavior() });
  }

  #behavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }
}

if (!customElements.get('highlight-scroll-band')) {
  customElements.define('highlight-scroll-band', HighlightScrollBand);
}
